import {
  PlaygroundLocalMsg,
  usePlaygroundLocalMsg,
} from "@faire/prototype-playground/platform/localization";
import { ChevronDown } from "@faire/slate-icons/ChevronDown";
import { tsv } from "@faire/slate/lib/tsv";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { MORE_IDEAS_SECTION_ID } from "./moreIdeas";
import { getNavigationHeaderBottom } from "./navigationHeaderPosition";

interface MoreIdeasFabProps {
  /** Thumbnails of the first recommendations, fanned on the left of the pill. */
  imageUrls: string[];
}

// Pill width before the label is revealed: left padding + the image fan.
const COLLAPSED_WIDTH_PX = 60;
const SCROLL_GAP_PX = 16;
// Let the page settle before the pill makes its entrance.
const ENTRANCE_DELAY_MS = 1000;

/**
 * Floating "More ideas" pill pinned to the bottom of the viewport (Figma
 * 9642:288791). On mount it rises from below, widens to reveal its label, and
 * fans its two thumbnails open. Tapping it scrolls to the "More ideas you
 * might like" section and drops the pill off-screen; it also stays hidden
 * while that section is already in view.
 */
export const MoreIdeasFab = ({ imageUrls }: MoreIdeasFabProps) => {
  const prefersReducedMotion = useReducedMotion();
  const contentRef = useRef<HTMLSpanElement>(null);
  const [expandedWidth, setExpandedWidth] = useState<number | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasEntranceDelayElapsed, setHasEntranceDelayElapsed] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setHasEntranceDelayElapsed(true),
      ENTRANCE_DELAY_MS
    );
    return () => window.clearTimeout(timeout);
  }, []);
  const [isTargetInView, setIsTargetInView] = useState(false);
  const label = usePlaygroundLocalMsg("Scroll to more ideas you might like");

  useLayoutEffect(() => {
    if (contentRef.current) {
      setExpandedWidth(contentRef.current.offsetWidth);
    }
  }, [imageUrls]);

  useEffect(() => {
    const target = document.getElementById(MORE_IDEAS_SECTION_ID);
    if (!target) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setIsTargetInView(entry?.isIntersecting ?? false),
      { rootMargin: `-${getNavigationHeaderBottom()}px 0px 0px 0px` }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const isVisible = hasEntranceDelayElapsed && !isDismissed && !isTargetInView;
  const fanImages = imageUrls.slice(0, 2);

  const handleClick = () => {
    setIsDismissed(true);
    const target = document.getElementById(MORE_IDEAS_SECTION_ID);
    if (!target) {
      return;
    }
    const top =
      target.getBoundingClientRect().top +
      window.scrollY -
      getNavigationHeaderBottom() -
      SCROLL_GAP_PX;
    window.scrollTo({
      top,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  const springTransition = prefersReducedMotion
    ? { duration: 0 }
    : { type: "spring" as const, stiffness: 320, damping: 30 };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center">
      <motion.button
        type="button"
        aria-label={label}
        aria-hidden={!isVisible}
        tabIndex={isVisible ? 0 : -1}
        onClick={handleClick}
        className={fabStyle.cls({ interactive: isVisible })}
        initial={{ y: 96, opacity: 0, width: COLLAPSED_WIDTH_PX }}
        animate={
          isVisible
            ? { y: 0, opacity: 1, width: expandedWidth ?? COLLAPSED_WIDTH_PX }
            : { y: 120, opacity: 0, width: expandedWidth ?? COLLAPSED_WIDTH_PX }
        }
        transition={{
          y: springTransition,
          opacity: { duration: prefersReducedMotion ? 0 : 0.25 },
          width: prefersReducedMotion
            ? { duration: 0 }
            : { delay: 0.3, duration: 0.45, ease: [0.4, 0, 0.2, 1] },
        }}
      >
        <span
          ref={contentRef}
          className="flex shrink-0 items-center gap-2 pr-4 pl-4 whitespace-nowrap"
        >
          <span className="relative h-7 w-9 shrink-0" aria-hidden>
            {fanImages.map((imageUrl, index) => (
              <motion.span
                key={imageUrl}
                className={fanImageStyle.cls({ back: index === 0 })}
                initial={{ x: 6, y: 1, rotate: 0 }}
                animate={
                  isVisible
                    ? index === 0
                      ? { x: 0, y: 2, rotate: -10 }
                      : { x: 12, y: 0, rotate: 9 }
                    : { x: 6, y: 1, rotate: 0 }
                }
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : {
                        delay: 0.55,
                        type: "spring",
                        stiffness: 260,
                        damping: 18,
                      }
                }
              >
                <img src={imageUrl} alt="" className="size-full object-cover" />
              </motion.span>
            ))}
          </span>
          <span className="type-fs-paragraph-medium text-fs-text-primary">
            <PlaygroundLocalMsg defaultMessage="More ideas" />
          </span>
          <span className="text-fs-text-primary flex shrink-0">
            <ChevronDown width="16" height="16" />
          </span>
        </span>
      </motion.button>
    </div>
  );
};

const fabStyle = tsv({
  base: [
    "bg-fs-action-surface-inverse flex h-10 items-center overflow-hidden rounded-full",
    "shadow-[0_2px_12px_rgba(0,0,0,0.15)]",
  ],
  variants: {
    interactive: {
      true: "pointer-events-auto",
      false: "pointer-events-none",
    },
  },
});

const fanImageStyle = tsv({
  base: [
    "absolute top-0 left-0 block size-6 overflow-hidden rounded",
    "ring-1 ring-(--slate-color-grey-1000)/[0.06]",
  ],
  variants: {
    back: {
      true: "",
      false: "shadow-[-1px_2px_2px_rgba(0,0,0,0.12)]",
    },
  },
});
